/**
 * `tour/useTour.ts` — when the first-visit spotlight tour shows (#2048).
 *
 * - Automatically, once per account, the first time a lead or co-lead
 *   lands on a team home. Remembered in localStorage per account id.
 * - Never inside the ephemeral demo (`/demo` sessions) or for members.
 * - Not while the page is still loading: after a short settle delay it
 *   waits (one animation frame at a time, up to ~3 s) for the shell's
 *   anchors and for the page's data; if that never happens it gives up
 *   quietly and tries again on the next visit.
 * - On request: the top bar's Help menu ("Show me around") calls `openTour()`.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { WorkspaceRole } from "../../lib/activeWorkspace";
import { isDemoPath } from "../../lib/auth";
import { readDemoSession } from "../../lib/demoSession";
import { findAnchor, isAnchorShown } from "./tourSteps";

const SEEN_KEY_PREFIX = "tarii.spotlight_tour_seen.v1:";
const OPEN_EVENT = "tarii:open-spotlight-tour";

/** Wait this long after landing before the tour may show. */
export const AUTO_SHOW_DELAY_MS = 600;
/** Give up waiting for the page to settle after this long. */
export const AUTO_SHOW_MAX_WAIT_MS = 3000;

export function tourSeenKey(accountId: string): string {
  return `${SEEN_KEY_PREFIX}${accountId}`;
}

export function hasSeenTour(accountId: string): boolean {
  try {
    return window.localStorage.getItem(tourSeenKey(accountId)) === "1";
  } catch {
    // Storage blocked — treat as seen so the tour never nags on every load.
    return true;
  }
}

export function markTourSeen(accountId: string): void {
  try {
    window.localStorage.setItem(tourSeenKey(accountId), "1");
  } catch {
    // Storage blocked — nothing to remember with.
  }
}

/** Start the tour from step 1, whatever the stored flag says. */
export function openTour(): void {
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
}

/** Call `onOpen` whenever something asks for the tour. */
export function useTourOpenRequests(onOpen: () => void): void {
  const ref = useRef(onOpen);
  ref.current = onOpen;
  useEffect(() => {
    const handler = () => ref.current();
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);
}

/** A team home: `/teams/<id>` with nothing after it. */
export function isTeamHomePath(pathname: string): boolean {
  return /^\/teams\/[^/]+\/?$/.test(pathname);
}

export function isTourLeadRole(role: WorkspaceRole | null | undefined): boolean {
  return role === "lead" || role === "co_lead";
}

/** Should the tour show by itself here, for this account? */
export function shouldAutoShowTour({
  accountId,
  role,
  pathname,
}: {
  accountId: string | null | undefined;
  role: WorkspaceRole | null | undefined;
  pathname: string;
}): boolean {
  if (!accountId || !isTourLeadRole(role)) return false;
  if (!isTeamHomePath(pathname) || isDemoPath(pathname)) return false;
  let demoSession: unknown = null;
  try {
    demoSession = readDemoSession();
  } catch {
    demoSession = null;
  }
  if (demoSession) return false;
  return !hasSeenTour(accountId);
}

/** The shell's anchors are on screen and the page is not still loading. */
function pageSettled(isFetching: () => number): boolean {
  const shellReady = ["team-switcher", "nav-roster", "nav-home", "panel-toggle"].some((a) => {
    const el = findAnchor(a);
    return el ? isAnchorShown(el) : false;
  });
  if (!shellReady) return false;
  const main = document.querySelector('[data-testid="appshell-main"]') ?? document.body;
  if (main.querySelector(".skeleton")) return false;
  return isFetching() === 0;
}

/**
 * Show the tour by itself once: `onShow` runs (and the flag is written)
 * when the page has settled. Re-arms on every new team home visit, so a
 * give-up on a slow load just means it tries again next time.
 */
export function useTourAutoShow({
  accountId,
  role,
  pathname,
  onShow,
  isSettled,
}: {
  accountId: string | null | undefined;
  role: WorkspaceRole | null | undefined;
  pathname: string;
  onShow: () => void;
  /** Test seam: replaces the "page has settled" check. */
  isSettled?: (() => boolean) | undefined;
}): void {
  const queryClient = useQueryClient();
  const onShowRef = useRef(onShow);
  onShowRef.current = onShow;
  const isSettledRef = useRef(isSettled);
  isSettledRef.current = isSettled;
  const eligible = shouldAutoShowTour({ accountId, role, pathname });

  useEffect(() => {
    if (!eligible || !accountId) return;
    let raf = 0;
    let cancelled = false;
    const settled = () =>
      isSettledRef.current ? isSettledRef.current() : pageSettled(() => queryClient.isFetching());
    const timer = window.setTimeout(() => {
      const start = performance.now();
      const tick = () => {
        if (cancelled) return;
        if (hasSeenTour(accountId)) return;
        if (settled()) {
          markTourSeen(accountId);
          onShowRef.current();
          return;
        }
        if (performance.now() - start >= AUTO_SHOW_MAX_WAIT_MS) return; // give up quietly
        raf = window.requestAnimationFrame(tick);
      };
      tick();
    }, AUTO_SHOW_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.cancelAnimationFrame(raf);
    };
  }, [eligible, accountId, queryClient]);
}
